<template>
  <AssetCard :view-mode="viewMode" :empty-title="materialEntity ? null : 'Aucune entité matérielle associée'"
    :empty-message="null" :show-edit-button="showEditButton" :show-delete-button="showDeleteButton"
    :show-duplicate-button="showDuplicateButton" :created-at="materialEntity?.created_at ?? null"
    :memos-count="memosCount" :table-grid-class="'flex items-center'" :is-owned="isOwned" :is-public="isPublic"
    :is-private="!isPublic" :show-ownership-badge="showOwnershipBadge" :user-id="userId"
    :can-edit="canEditMaterialEntity" :can-delete="canDeleteMaterialEntity" @click="handleClick" @edit="handleEdit"
    @delete="handleDelete" @duplicate="handleDuplicate">
    <!-- Mode Table -->
    <template #table-row="{ classes }">
      <!-- Appellation et identifiant -->
      <div class="flex items-center gap-2 min-w-0 flex-[1.25]">
        <Amphora :class="classes.icon" />
        <div class="min-w-0">
          <div class="flex items-center gap-2">
            <span :class="[classes.title, 'truncate']">{{ materialEntity?.appellation || 'Sans nom' }}</span>
            <span v-if="materialEntity?.identifier" class="text-xs text-gray-500">({{ materialEntity.identifier
              }})</span>
          </div>
        </div>
      </div>

      <!-- Type -->
      <div class="hidden md:flex items-center text-sm text-gray-600 min-w-0 flex-1 px-3">
        <span class="truncate">{{ materialEntity?.type || 'Non spécifié' }}</span>
      </div>

      <!-- Matériau -->
      <div class="hidden lg:flex items-center text-sm text-gray-600 min-w-0 flex-1 px-3">
        <span class="truncate">{{ materialEntity?.material || 'Non spécifié' }}</span>
      </div>

      <!-- Provenance -->
      <div class="flex items-center text-sm text-gray-600 min-w-0 flex-1 px-3">
        <span class="truncate">{{ materialEntity?.provenance_location?.site || 'Non spécifié' }}</span>
      </div>

      <!-- Nombre de mémos -->
      <div class="flex items-center gap-1 text-sm text-gray-600 min-w-0 px-3">
        <ClipboardIcon :class="classes.footerIcon" />
        <span>{{ memosCount }}</span>
      </div>

      <!-- Actions -->
      <TableRowActions :classes="classes" :show-edit-button="showEditButton" :show-delete-button="showDeleteButton"
        :show-duplicate-button="showDuplicateButton" :can-edit="canEditMaterialEntity"
        :can-delete="canDeleteMaterialEntity" @edit="handleEdit" @delete="handleDelete" @duplicate="handleDuplicate" />
    </template>

    <!-- Mode Memos Table -->
    <template #memos-table-icon="{ classes }">
      <Amphora :class="classes.icon" />
    </template>

    <template #memos-table-content>
      <div v-if="materialEntity" class="min-w-0 flex-1">
        <div class="flex items-center gap-2">
          <h3 class="font-medium text-gray-900 text-lg truncate">{{ materialEntity.appellation }}</h3>
          <span v-if="materialEntity.identifier" class="text-gray-500">({{ materialEntity.identifier }})</span>
        </div>
        <div class="flex items-center gap-2 text-xs text-gray-500">
          <span v-if="materialEntity.type">{{ materialEntity.type }}</span>
          <span v-if="materialEntity.type && materialEntity.material" class="text-gray-300 shrink-0">•</span>
          <span v-if="materialEntity.material">{{ materialEntity.material }}</span>
          <span v-if="materialEntity.provenance_location?.site" class="text-gray-300 shrink-0">•</span>
          <span v-if="materialEntity.provenance_location?.site" class="truncate">{{
            materialEntity.provenance_location.site }}</span>
        </div>
      </div>
      <div v-else class="text-gray-500 text-sm flex items-center gap-2">
        <Amphora class="h-4 w-4 text-gray-400" />
        <span>Aucune entité matérielle associée</span>
      </div>
    </template>

    <!-- Mode Default -->
    <template #default="{ classes }">
      <div class="flex items-center gap-4 mb-2">
        <h2 :class="classes.title">{{ materialEntity?.appellation || 'Sans titre' }}</h2>
        <span v-if="materialEntity?.identifier" class="text-sm text-gray-500">({{ materialEntity.identifier }})</span>
      </div>
    </template>

    <!-- Mode Default Info -->
    <template #info="{ classes }">
      <div v-if="materialEntity?.provenance_location" :class="classes.iconWithText">
        <MapPin :class="classes.icon" />
        <span>{{ materialEntity.provenance_location.site }}
          <span v-if="materialEntity.provenance_location.place" class="text-gray-500">
            - {{ materialEntity.provenance_location.place }}
          </span>
        </span>
      </div>

      <div v-if="materialEntity?.type" :class="classes.iconWithText">
        <Tag :class="classes.icon" />
        <span>{{ materialEntity.type }}</span>
      </div>

      <div v-if="materialEntity?.material" :class="classes.iconWithText">
        <Package :class="classes.icon" />
        <span>{{ materialEntity.material }}</span>
      </div>
    </template>

  </AssetCard>
</template>

<script lang="ts" setup>
import {
  Amphora,
  Clipboard as ClipboardIcon,
  MapPin,
  Package,
  Tag
} from 'lucide-vue-next';
import type { PropType } from 'vue';
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import type { MaterialEntity } from '../../api/types/entities/materialEntity/types';
import { useAuthStore } from '../../stores/auth';
import { useBreadcrumbsStore } from '../../stores/breadcrumbs';
import AssetCard from '../UI/AssetCard.vue';
import TableRowActions from '../UI/TableRowActions.vue';

const props = defineProps({
  materialEntity: {
    type: Object as PropType<MaterialEntity | null>,
    default: null
  },
  viewMode: {
    type: String as PropType<'default' | 'table' | 'memostable'>,
    default: 'default'
  },
  showActions: {
    type: Boolean,
    default: true
  },
  showEditButton: {
    type: Boolean,
    default: false
  },
  showDeleteButton: {
    type: Boolean,
    default: false
  },
  showDuplicateButton: {
    type: Boolean,
    default: false
  },
  // Nouvelles props pour les permissions et propriété
  isOwned: {
    type: Boolean,
    default: false
  },
  isPublic: {
    type: Boolean,
    default: false
  },
  showOwnershipBadge: {
    type: Boolean,
    default: true
  },
  userId: {
    type: String,
    default: undefined
  }
});

const emit = defineEmits(['edit', 'delete', 'duplicate']);

const router = useRouter();
const breadcrumbsStore = useBreadcrumbsStore();
const authStore = useAuthStore();

const memosCount = computed(() => {
  if (props.materialEntity && 'memos' in props.materialEntity && Array.isArray(props.materialEntity.memos)) {
    return props.materialEntity.memos.length;
  }
  return 0;
});

// Computed pour les permissions
const canEditMaterialEntity = computed((): boolean => {
  return props.materialEntity ? authStore.canEditAsset(props.materialEntity as any) : false;
});

const canDeleteMaterialEntity = computed((): boolean => {
  return props.materialEntity ? authStore.canDeleteAsset(props.materialEntity as any) : false;
});

const handleClick = (): void => {
  if (!props.materialEntity) return;

  try {
    // Sauvegarder l'état minimal dans l'historique
    const minimalData = {
      uuid: props.materialEntity.uuid,
      appellation: props.materialEntity.appellation || '',
      type: props.materialEntity.type || '',
      material: props.materialEntity.material || ''
    };

    history.replaceState({ materialEntity: minimalData }, '');

    // Mettre à jour le breadcrumb avec le nom de l'entité matérielle
    const entityName = props.materialEntity.appellation || 'Entité matérielle';
    breadcrumbsStore.updateBreadcrumbs([
      { name: 'Mes entités matérielles', path: '/material-entities' },
      { name: entityName, path: `/material-entity/${props.materialEntity.uuid}` }
    ]);

    // Naviguer vers la page de détail
    router.push(`/material-entity/${props.materialEntity.uuid}`).catch((error) => {
      console.error('Erreur lors de la navigation:', error);
    });
  } catch (error) {
    console.error('Erreur lors de la navigation:', error);
  }
};

const handleEdit = (): void => {
  if (props.materialEntity) {
    emit('edit', props.materialEntity);
  }
};

const handleDelete = (): void => {
  if (props.materialEntity?.uuid) {
    emit('delete', props.materialEntity.uuid);
  }
};

const handleDuplicate = (): void => {
  if (props.materialEntity) {
    emit('duplicate', props.materialEntity);
  }
};
</script>